import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  InputAdornment,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import { instanceService } from "../../services";

interface CredentialsDisplayProps {
  instanceId: string;
  username?: string;
  hasCredentials: boolean;
}

const CredentialsDisplay: React.FC<CredentialsDisplayProps> = ({
  instanceId,
  username,
  hasCredentials,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState<"username" | "password" | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);

  const handleCopy = async (value: string, type: "username" | "password") => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleRevealCredentials = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await instanceService.getCredentials(instanceId);
      if (response.success) {
        setPassword(response.password);
        setRevealed(true);
      } else {
        setError(response.message || "Failed to retrieve credentials");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to retrieve credentials",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasCredentials && !username) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
        }}
      >
        No credentials available
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Credentials
      </Typography>
      {username && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            gutterBottom
            sx={{
              color: "text.secondary",
            }}
          >
            Username
          </Typography>
          <TextField
            value={username}
            size="small"
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
                sx: { fontFamily: "monospace" },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied === "username" ? "Copied!" : "Copy"}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(username, "username")}
                        edge="end"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      )}
      {hasCredentials && (
        <Box>
          <Typography
            variant="body2"
            gutterBottom
            sx={{
              color: "text.secondary",
            }}
          >
            Password
          </Typography>

          {!revealed ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={
                  isLoading ? <CircularProgress size={16} /> : <LockIcon />
                }
                onClick={handleRevealCredentials}
                disabled={isLoading}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
              >
                {isLoading ? "Loading..." : "Reveal Password"}
              </Button>
              {error && (
                <Alert severity="error" sx={{ py: 0.5 }}>
                  {error}
                </Alert>
              )}
            </Box>
          ) : (
            <TextField
              value={password || ""}
              type={showPassword ? "text" : "password"}
              size="small"
              fullWidth
              slotProps={{
                input: {
                  readOnly: true,
                  sx: { fontFamily: "monospace" },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? "Hide" : "Show"}>
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={copied === "password" ? "Copied!" : "Copy"}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            password && handleCopy(password, "password")
                          }
                          edge="end"
                          disabled={!password}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CredentialsDisplay;
